import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { setLoading } from '../reducers/loadingReducer';
import { updateUserProfile } from '../services/apiService';
import { updateUserProfileInfo } from '../reducers/authReducer';
import LoadingSpinner from './LoadingSpinner';
import PasswordField from './PasswordField';
import useResponsive from '../hooks/useResponsive';

const ProfileUserInfo = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loading);
  const user = useSelector((state) => state.auth.user);
  const [editMode, setEditMode] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [userLocal, setUser] = useState(user);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});

  const { isMobileScreen } = useResponsive();

  const handleEditToggle = () => setEditMode(!editMode);
  const handleUserChange = (e) => setUser({ ...userLocal, [e.target.name]: e.target.value });
  const handleOpenPasswordDialog = () => setOpenPasswordDialog(true);
  const handleClosePasswordDialog = () => {
    setPassword('');
    setPasswordConfirm('');
    setOpenPasswordDialog(false);
  };

  const validate = () => {
    let tempErrors = {};
    tempErrors.firstname = userLocal.firstname ? '' : 'First Name is required.';
    tempErrors.lastname = userLocal.lastname ? '' : 'Last Name is required.';
    tempErrors.email = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(userLocal.email) ? '' : 'Email is not valid.';
    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (validate()) {
      dispatch(setLoading(true));
      try {
        await updateUserProfile(user.userId, {
          firstname: userLocal.firstname,
          lastname: userLocal.lastname,
          email: userLocal.email,
          userId: user.userId,
        });
        dispatch(updateUserProfileInfo(userLocal));
        setEditMode(false);
      } catch (error) {
        console.error('Failed to update user profile:', error);
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  return (
    <Box
      sx={{
        margin: 1,
        width: '100%',
        padding: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography variant="h6" align="center" gutterBottom>
        Account Info
      </Typography>

      <Box
        component="form"
        onSubmit={handleSave}
        sx={{
          margin: 1,
          width: '100%',
          maxWidth: 600,
          padding: 3,
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: 2,
        }}
      >
        <TextField
          fullWidth
          margin="normal"
          label="First Name"
          name="firstname"
          value={userLocal.firstname}
          onChange={handleUserChange}
          disabled={!editMode || loading}
          error={!!errors.firstname}
          helperText={errors.firstname}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Last Name"
          name="lastname"
          value={userLocal.lastname}
          onChange={handleUserChange}
          disabled={!editMode || loading}
          error={!!errors.lastname}
          helperText={errors.lastname}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Email Address"
          name="email"
          value={userLocal.email}
          onChange={handleUserChange}
          disabled={!editMode || loading}
          error={!!errors.email}
          helperText={errors.email}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobileScreen ? 'column' : 'row',
            justifyContent: 'space-between',
            mt: 2,
            gap: 2,
          }}
        >
          {loading ? (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              {!editMode && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleOpenPasswordDialog}
                >
                  Reset Password
                </Button>
              )}

              {editMode ? (
                <Box>
                  <Button variant="contained" type="submit" sx={{ mr: 1 }}>
                    Save
                  </Button>
                  <Button onClick={handleEditToggle}>Cancel</Button>
                </Box>
              ) : (
                <Button variant="contained" onClick={handleEditToggle}>
                  Edit
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}>
        <DialogTitle>
          Reset Password
          <IconButton
            onClick={handleClosePasswordDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PasswordField
            fullWidth
            margin="normal"
            label="New Password"
            name="newPassword"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordField
            fullWidth
            margin="normal"
            label="Confirm Password"
            name="confirmPassword"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileUserInfo;
